// runStory — derives the run's STORY from existing store data.
//
// The canvas renders state (visited/active nodes) but the story of a run —
// where the path deliberately ends, in what order nodes were walked, which
// assertions ran where — is derived here as pure functions + memoized
// selectors. Nothing in this module fabricates data: every terminus reason
// maps to a real assertion record or simulation status already in the store.

import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../../store/index.ts';
import {
  selectActualPath,
  selectSimulationStatus,
  selectEffectiveAssertionResults,
  type AssertionResult,
} from '../../store/slices/simulationSlice.ts';
import type { SimulationStatus } from '../../types/simulation-results.ts';
import type { BranchPredicate } from '../../types/index.ts';

// ---------------------------------------------------------------------------
// Terminus — why did the path end where it did?
// ---------------------------------------------------------------------------

/**
 * - 'terminal-end'  — terminal_state assertion PASSED at the last step
 *                     (`terminal-N-final`): the flow reached a declared end.
 * - 'declared-stop' — the run aborted exactly where the scenario declared it
 *                     would (`terminal-N-declared-failure`): an EXPECTED stop.
 * - 'aborted'       — simulationStatus 'failed': the run broke off early.
 * - 'neutral-end'   — completed, but the last node carries no terminal flag
 *                     (e.g. old-format artifacts omit terminal markers).
 */
export type TerminusReason = 'terminal-end' | 'declared-stop' | 'aborted' | 'neutral-end';

export interface TerminusInfo {
  reason: TerminusReason;
  /** Last node of actualPath (scenario-namespace ID). */
  nodeId: string;
  /** 0-based index of the last path step. */
  stepIndex: number;
  /** Short text rendered inside the badge. */
  badgeText: string;
  /** Full human-readable reason — used as the badge tooltip. */
  title: string;
  /** Terminal condition (branch route name) when derivable, else null. */
  condition: string | null;
}

/**
 * Derive the terminal condition for a node from its branch marker.
 * Moment node IDs encode the taken branch as a `::brN` suffix; N indexes the
 * predicate's route-key order (first route condition → br0). Returns null
 * when the node carries no marker or no predicate exists for its frame —
 * callers may fall back to the scenario-label bracket.
 */
export function branchConditionOf(
  nodeId: string,
  branchPredicates: Record<string, BranchPredicate>,
): string | null {
  const marker = nodeId.match(/::br(\d+)$/);
  if (!marker) return null;
  const frameId = nodeId.split('::')[0];
  const predicate = branchPredicates[frameId];
  if (!predicate) return null;
  return Object.keys(predicate.routes)[Number(marker[1])] ?? null;
}

/** Extract a bracketed condition from a scenario label ("… [SlotTaken]"). */
export function scenarioLabelBracket(label: string | null): string | null {
  if (!label) return null;
  const m = label.match(/\[([^\]]+)\]/);
  return m ? m[1].trim() : null;
}

export interface TerminusInputs {
  actualPath: string[];
  status: SimulationStatus;
  results: AssertionResult[];
  branchPredicates: Record<string, BranchPredicate>;
  scenarioLabel: string | null;
  /** Events consumed by the walker (event-index space, not path space). */
  walkerPosition: number;
  totalEvents: number;
}

/**
 * Derive the terminus for the current run, or null when the run has not
 * reached a terminal status (nothing renders during playback — no layout
 * jumps) or when the recorded state no longer corresponds to a finished
 * walk (e.g. scrubbed backward after completion).
 */
export function deriveTerminus(inputs: TerminusInputs): TerminusInfo | null {
  const { actualPath, status, results, branchPredicates, scenarioLabel } = inputs;
  if (actualPath.length === 0) return null;

  const stepIndex = actualPath.length - 1;
  const nodeId = actualPath[stepIndex];
  const stepLabel = `step ${stepIndex + 1} of ${actualPath.length}`;

  if (status === 'failed') {
    const at = `event ${inputs.walkerPosition} of ${inputs.totalEvents}`;
    return {
      reason: 'aborted',
      nodeId,
      stepIndex,
      badgeText: `⚠ ABORTED at ${at}`,
      title: `Run aborted at ${at} — the walk broke off at "${nodeId}" before completing. Assertions are incomplete and must not read as a pass.`,
      condition: null,
    };
  }

  if (status !== 'completed') return null;

  const declared = results.find(
    (r) => r.assertionId === `terminal-${stepIndex}-declared-failure` && r.passed,
  );
  if (declared) {
    return {
      reason: 'declared-stop',
      nodeId,
      stepIndex,
      badgeText: '⛔ STOP · precondition refused',
      title: `Expected stop (${stepLabel}): ${declared.detail}`,
      condition: null,
    };
  }

  const final = results.find((r) => r.assertionId === `terminal-${stepIndex}-final` && r.passed);
  if (final) {
    const condition =
      branchConditionOf(nodeId, branchPredicates) ?? scenarioLabelBracket(scenarioLabel);
    return {
      reason: 'terminal-end',
      nodeId,
      stepIndex,
      badgeText: condition ? `⏹ END · ${condition}` : '⏹ END',
      title:
        `Terminal state reached (${stepLabel}): ${final.detail}` +
        (condition ? ` — terminal condition "${condition}".` : ''),
      condition,
    };
  }

  // Neutral END: completed with a non-terminal last node. Only shown when the
  // terminal tier actually evaluated this step (old-format skip/fail records)
  // or the walker consumed every event — never for a scrubbed-back path.
  const hasTerminalRecord = results.some((r) => r.assertionId.startsWith(`terminal-${stepIndex}-`));
  if (hasTerminalRecord || inputs.walkerPosition >= inputs.totalEvents) {
    return {
      reason: 'neutral-end',
      nodeId,
      stepIndex,
      badgeText: '⏹ END',
      title: `Flow completed (${stepLabel}) at "${nodeId}" — the final event carries no [terminal] declaration in the spec, so the end is recorded neutrally rather than as a declared terminal state.`,
      condition: null,
    };
  }

  return null;
}

export const selectTerminus = createSelector(
  [
    selectActualPath,
    selectSimulationStatus,
    selectEffectiveAssertionResults,
    (state: RootState) => state.topology.branchPredicates,
    (state: RootState) => state.events.scenarioLabel,
    (state: RootState) => state.simulation.walkerPosition,
    (state: RootState) => state.events.events.length,
  ],
  (actualPath, status, results, branchPredicates, scenarioLabel, walkerPosition, totalEvents) =>
    deriveTerminus({
      actualPath,
      status,
      results,
      branchPredicates,
      scenarioLabel,
      walkerPosition,
      totalEvents,
    }),
);

// ---------------------------------------------------------------------------
// Sequence chips — 1-based step order, revisits joined with ↩
// ---------------------------------------------------------------------------

/**
 * Format the 0-based actualPath indices at which a node was visited into a
 * 1-based chip label. A node visited multiple times (returns-to unroll)
 * shows all indices joined: [3, 11] → "4↩12".
 */
export function formatStepChip(stepIndices: number[]): string {
  return stepIndices.map((i) => i + 1).join('↩');
}

// ---------------------------------------------------------------------------
// Per-node assertion pills
// ---------------------------------------------------------------------------

export interface NodeAssertionPill {
  kind: 'pass' | 'skip' | 'fail';
  count: number;
  glyph: string;
}

/** Build the pill row for a node — only non-zero pills, ✓ then — then ✗. */
export function buildAssertionPills(
  passedCount: number,
  skipCount: number,
  failCount: number,
): NodeAssertionPill[] {
  const pills: NodeAssertionPill[] = [];
  if (passedCount > 0) pills.push({ kind: 'pass', count: passedCount, glyph: '✓' });
  if (skipCount > 0) pills.push({ kind: 'skip', count: skipCount, glyph: '—' });
  if (failCount > 0) pills.push({ kind: 'fail', count: failCount, glyph: '✗' });
  return pills;
}
